import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Package, Search, Download, Filter, BarChart3, PieChart, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * Reusable Closing Stock Report component for all sections
 * 
 * @param {string} sectionName - Display name for the section (e.g., "FOB", "Greige", "Goods Transfer")
 * @param {string} backRoute - Route to navigate back to (e.g., "/sections/fob")
 * @param {Function} fetchClosingStock - API function to fetch closing stock report
 * @param {Function} fetchOperations - API function to fetch available operations
 * @param {string} exportPrefix - Prefix for exported CSV filename (e.g., "fob", "greige")
 * @param {string} summaryGroupBy - What to group by in summary view: "document" | "design" | "operation"
 * @param {boolean} showBundleCount - Whether to show bundle count column (default: false)
 */
const SectionClosingStockReport = ({
  sectionName,
  backRoute,
  fetchClosingStock,
  fetchOperations,
  exportPrefix,
  summaryGroupBy = 'design', // 'document', 'design', or 'operation'
  showBundleCount = false
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState([]);
  const [totalUsns, setTotalUsns] = useState(0);
  const [totalBundles, setTotalBundles] = useState(0);
  const [totalDesigns, setTotalDesigns] = useState(0);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOperation, setSelectedOperation] = useState('all');
  const [viewMode, setViewMode] = useState('summary');
  const [operations, setOperations] = useState([]);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const [reportData, opsData] = await Promise.all([
        fetchClosingStock({
          search: searchQuery || undefined,
          operation: selectedOperation === 'all' ? undefined : selectedOperation,
          view_mode: viewMode
        }),
        fetchOperations()
      ]);
      
      setReport(reportData.report || []);
      setTotalUsns(reportData.total_usns || 0);
      setTotalBundles(reportData.total_bundles || 0);
      setTotalDesigns(reportData.total_designs || new Set((reportData.report || []).map(r => r.design_name)).size);
      setOperations(opsData.operations || []);
    } catch (error) {
      console.error('Failed to fetch closing stock report:', error);
      toast.error('Failed to fetch closing stock report');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedOperation, viewMode, fetchClosingStock, fetchOperations]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedOperation('all');
  };

  const formatOperationName = (opName) => {
    if (!opName) return 'Unknown';
    return opName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleExportCSV = () => {
    try {
      setExporting(true);
      
      if (report.length === 0) {
        toast.warning('No data to export');
        return;
      }
      
      let headers, csvRows;
      if (viewMode === 'detailed') {
        headers = ['Barcode', 'Design Name', 'Color', 'Operation Name', 'Closing Qty'];
        csvRows = [headers.join(',')];
        report.forEach(row => {
          csvRows.push([
            `"${row.barcode || ''}"`,
            `"${row.design_name || ''}"`,
            `"${row.color || ''}"`,
            `"${formatOperationName(row.operation_name)}"`,
            row.closing_qty || 0
          ].join(','));
        });
      } else {
        // Summary view - varies by summaryGroupBy
        if (summaryGroupBy === 'document') {
          headers = ['Document Barcode', 'Operation Name', 'USN Count', 'Bundle Count'];
          csvRows = [headers.join(',')];
          report.forEach(row => {
            csvRows.push([
              `"${row.document_barcode || ''}"`,
              `"${formatOperationName(row.operation_name)}"`,
              row.usn_count || 0,
              row.bundle_count || 0
            ].join(','));
          });
        } else if (summaryGroupBy === 'operation') {
          headers = ['Operation Name', 'USN Count', 'Bundle Count'];
          csvRows = [headers.join(',')];
          report.forEach(row => {
            csvRows.push([
              `"${formatOperationName(row.operation_name)}"`,
              row.usn_count || 0,
              row.bundle_count || 0
            ].join(','));
          });
        } else {
          headers = ['Design Name', 'Operation Name', 'USN Count'];
          if (showBundleCount) headers.push('Bundle Count');
          csvRows = [headers.join(',')];
          report.forEach(row => {
            const rowData = [
              `"${row.design_name || ''}"`,
              `"${formatOperationName(row.operation_name)}"`,
              row.usn_count || 0
            ];
            if (showBundleCount) rowData.push(row.bundle_count || 0);
            csvRows.push(rowData.join(','));
          });
        }
      }
      
      csvRows.push(['']);
      csvRows.push([`"Total"`, '', totalUsns, showBundleCount ? totalBundles : ''].join(','));
      
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportPrefix}_closing_stock_${viewMode}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${report.length} records`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // Get third stat card config based on summaryGroupBy
  const getThirdStatConfig = () => {
    if (viewMode === 'detailed') {
      return { label: 'Records', value: report.length, icon: PieChart };
    }
    switch (summaryGroupBy) {
      case 'document':
        return { label: 'Documents', value: report.length, icon: PieChart };
      case 'operation':
        return { label: 'Operations', value: report.length, icon: PieChart };
      default:
        return { label: 'Design Types', value: totalDesigns, icon: Package };
    }
  };

  const thirdStat = getThirdStatConfig();

  // Render summary table based on groupBy type
  const renderSummaryTable = () => {
    if (summaryGroupBy === 'document') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">#</TableHead>
              <TableHead className="font-semibold">Document Barcode</TableHead>
              <TableHead className="font-semibold">Operation</TableHead>
              <TableHead className="font-semibold text-center">USN Count</TableHead>
              <TableHead className="font-semibold text-center">Bundles</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.map((row, idx) => (
              <TableRow key={`${row.document_barcode}-${row.operation_name}-${idx}`} className="hover:bg-gray-50">
                <TableCell className="text-gray-500">{idx + 1}</TableCell>
                <TableCell className="font-mono text-sm">{row.document_barcode}</TableCell>
                <TableCell><Badge variant="outline">{formatOperationName(row.operation_name)}</Badge></TableCell>
                <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0">{(row.usn_count || 0).toLocaleString()}</Badge></TableCell>
                <TableCell className="text-center">{row.bundle_count || 0}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-gray-100 font-semibold">
              <TableCell></TableCell>
              <TableCell className="font-bold">Total</TableCell>
              <TableCell></TableCell>
              <TableCell className="text-center"><Badge className="bg-[#2D5F4F] text-white border-0">{totalUsns.toLocaleString()}</Badge></TableCell>
              <TableCell className="text-center font-bold">{totalBundles}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      );
    }

    if (summaryGroupBy === 'operation') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">#</TableHead>
              <TableHead className="font-semibold">Operation Name</TableHead>
              <TableHead className="font-semibold text-center">Closing Stock</TableHead>
              <TableHead className="font-semibold text-center">Unit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.map((row, idx) => (
              <TableRow key={row.operation_name} className="hover:bg-gray-50">
                <TableCell className="text-gray-500">{idx + 1}</TableCell>
                <TableCell><Badge variant="outline">{formatOperationName(row.operation_name)}</Badge></TableCell>
                <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0">{(row.closing_count || 0).toLocaleString()}</Badge></TableCell>
                <TableCell className="text-center text-gray-500">{row.unit || 'USNs'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Default: group by design
    return (
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            <TableHead className="font-semibold">#</TableHead>
            <TableHead className="font-semibold">Design Name</TableHead>
            <TableHead className="font-semibold">Operation Name</TableHead>
            <TableHead className="font-semibold text-center">USN Count</TableHead>
            {showBundleCount && <TableHead className="font-semibold text-center">Bundles</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {report.map((row, idx) => (
            <TableRow key={`${row.design_name}-${row.operation_name}-${idx}`} className="hover:bg-gray-50">
              <TableCell className="text-gray-500">{idx + 1}</TableCell>
              <TableCell className="font-medium">{row.design_name || 'Unknown'}</TableCell>
              <TableCell><Badge variant="outline">{formatOperationName(row.operation_name)}</Badge></TableCell>
              <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0">{(row.usn_count || 0).toLocaleString()}</Badge></TableCell>
              {showBundleCount && <TableCell className="text-center">{row.bundle_count || 0}</TableCell>}
            </TableRow>
          ))}
          <TableRow className="bg-gray-100 font-semibold">
            <TableCell></TableCell>
            <TableCell className="font-bold">Total</TableCell>
            <TableCell></TableCell>
            <TableCell className="text-center"><Badge className="bg-[#2D5F4F] text-white border-0">{totalUsns.toLocaleString()}</Badge></TableCell>
            {showBundleCount && <TableCell className="text-center font-bold">{totalBundles}</TableCell>}
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Closing Stock Report</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionName} - Live Report</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-green-400 text-green-400">Live</Badge>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchReport}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Filters Section */}
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <Button 
                  variant={viewMode === 'summary' ? 'default' : 'ghost'} 
                  size="sm" 
                  onClick={() => setViewMode('summary')}
                  className={viewMode === 'summary' ? 'bg-[#2D5F4F]' : ''}
                >
                  <LayoutGrid className="w-4 h-4 mr-1" />Summary
                </Button>
                <Button 
                  variant={viewMode === 'detailed' ? 'default' : 'ghost'} 
                  size="sm" 
                  onClick={() => setViewMode('detailed')}
                  className={viewMode === 'detailed' ? 'bg-[#2D5F4F]' : ''}
                >
                  <List className="w-4 h-4 mr-1" />Detailed
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm font-medium mb-1.5 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search bundle, USN, or design..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchReport()}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Operation</Label>
                <Select value={selectedOperation} onValueChange={setSelectedOperation}>
                  <SelectTrigger data-testid="operation-filter">
                    <SelectValue placeholder="All Operations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Operations</SelectItem>
                    {operations.map(op => (
                      <SelectItem key={op} value={op}>{formatOperationName(op)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-end mt-4 pt-4 border-t border-[#E5DFD0] gap-2">
              <Button variant="outline" size="sm" onClick={handleClearFilters} data-testid="clear-filters-btn">
                Clear
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchReport}
                className="bg-[#2D5F4F] text-white hover:bg-[#1A1F2E]"
                data-testid="apply-filters-btn"
              >
                <Search className="w-4 h-4 mr-1" />Apply
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportCSV}
                disabled={exporting}
                className="border-green-600 text-green-600 hover:bg-green-50"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total USN Count</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{totalUsns.toLocaleString()}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Bundles</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{totalBundles.toLocaleString()}</p>
                </div>
                <Package className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5A5F6E]">{thirdStat.label}</p>
                  <p className="text-2xl font-bold text-[#1A1F2E]">{thirdStat.value.toLocaleString()}</p>
                </div>
                <thirdStat.icon className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : report.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">No Stock Found</h3>
              <p className="text-[#5A5F6E]">No items match the current filters</p>
            </CardContent>
          </Card>
        ) : viewMode === 'detailed' ? (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Closing Stock - Detailed View
                <Badge variant="outline" className="ml-2">{report.length} records</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">#</TableHead>
                      <TableHead className="font-semibold">Barcode</TableHead>
                      <TableHead className="font-semibold">Design Name</TableHead>
                      <TableHead className="font-semibold">Color</TableHead>
                      <TableHead className="font-semibold">Operation Name</TableHead>
                      <TableHead className="font-semibold text-center">Closing Qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.map((row, idx) => (
                      <TableRow key={`${row.barcode}-${idx}`} className="hover:bg-gray-50">
                        <TableCell className="text-gray-500">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{row.barcode}</TableCell>
                        <TableCell className="font-medium">{row.design_name || 'Unknown'}</TableCell>
                        <TableCell>{row.color || '-'}</TableCell>
                        <TableCell><Badge variant="outline">{formatOperationName(row.operation_name)}</Badge></TableCell>
                        <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700 border-0">{row.closing_qty}</Badge></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-100 font-semibold">
                      <TableCell></TableCell>
                      <TableCell colSpan={4} className="font-bold">Total</TableCell>
                      <TableCell className="text-center"><Badge className="bg-[#2D5F4F] text-white border-0">{totalUsns.toLocaleString()}</Badge></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Closing Stock - Summary
                <Badge variant="outline" className="ml-2">{report.length} records</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                {renderSummaryTable()}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SectionClosingStockReport;
